import BaseService from '../../../services/BaseService';
import FileService from '../../../services/FileService';
import { INSPECAO_MT_BT_ID } from './constants';

const mensagemErro = (error, fallback) =>
  error?.response?.data?.mensagem
  || error?.response?.data?.message
  || fallback;

export const listarProcedimentos = async ({ page, pageSize, filter }) => {
  const params = new URLSearchParams();
  params.set('p', String(page));
  params.set('s', String(pageSize));
  const texto = filter?.nome && String(filter.nome).trim();
  if (texto) params.set('texto', texto);
  // `equipe`, `prioridadeNsmp`, `acesso` e `acessoVeiculo` são pedido ao BE (contrato §1.1): até existirem, o servidor os ignora.
  ['malhaId', 'gerenciaId', 'poloId', 'subtipoId', 'tipoServicoId', 'equipe', 'prioridadeNsmp', 'acesso', 'acessoVeiculo'].forEach((key) => {
    const value = filter?.[key];
    if (value) params.set(key, value);
  });
  if (filter?.ativo === 'true' || filter?.ativo === 'false') {
    params.set('ativo', filter.ativo);
  }
  const response = await BaseService.get({ url: `procedimentos-digitacao?${params.toString()}` });
  const data = response?.data || {};
  return {
    rows: data.rows || [],
    totalRows: data.totalRows || 0,
  };
};

export const obterProcedimento = async (id) => {
  const response = await BaseService.get({ url: `procedimentos-digitacao/${id}` });
  return response?.data;
};

const acessoVeiculoParaApi = (valor) => {
  if (valor === true || valor === 'true') return true;
  if (valor === false || valor === 'false') return false;
  return null;
};

/** Critérios de NSMP do GET para o estado do formulário. Card antigo, sem `nsmp`, vira "vale para todos". */
export const valoresNsmpDe = (nsmp) => ({
  equipes: (nsmp?.equipes || []).map((item) => (typeof item === 'string' ? item : item?.equipe)).filter(Boolean),
  prioridadesNsmp: (nsmp?.prioridades || []).map(String),
  acessos: (nsmp?.acessos || []).map(String),
  acessoVeiculo: nsmp?.acessoVeiculo == null ? '' : String(nsmp.acessoVeiculo),
});

/** Detalhe do GET para o estado do formulário (sem anexos, que cada tela baixa do seu jeito). */
export const valoresDeDetalhe = (detalhe) => ({
  titulo: detalhe?.titulo || '',
  conteudoHtml: detalhe?.conteudoHtml || '',
  prioridade: detalhe?.prioridade || 'MEDIA',
  ativo: detalhe?.ativo !== false,
  desativadoEm: detalhe?.desativadoEm || null,
  desativadoPor: detalhe?.desativadoPor || '',
  subtipoIds: (detalhe?.inspecao?.subtipos || []).map((item) => item.id),
  malhaIds: (detalhe?.regiao?.malhas || []).map((item) => item.id),
  gerenciaIds: (detalhe?.regiao?.gerencias || []).map((item) => item.id),
  poloIds: (detalhe?.regiao?.polos || []).map((item) => item.id),
  tipoServicoIds: (detalhe?.servico?.tiposServico || []).map((item) => item.id),
  categorias: (detalhe?.servico?.categorias || []).map((item) => item.categoria),
  ...valoresNsmpDe(detalhe?.nsmp),
});

const payloadDe = (values) => ({
  titulo: values.titulo || '',
  conteudoHtml: values.conteudoHtml || '',
  prioridade: values.prioridade || 'MEDIA',
  ativo: values.ativo !== false,
  inspecao: {
    tipoInspecaoIds: [INSPECAO_MT_BT_ID],
    subtipoIds: values.subtipoIds || [],
  },
  regiao: {
    malhaIds: values.malhaIds || [],
    gerenciaIds: values.gerenciaIds || [],
    poloIds: values.poloIds || [],
  },
  servico: {
    tipoServicoIds: values.tipoServicoIds || [],
    // Sem campo na tela (a categoria é pós-digitação), mas o PUT substitui: reenvia o que veio do GET.
    categorias: values.categorias || [],
  },
  // O PUT substitui o bloco inteiro: sempre enviar, mesmo vazio, ou a edição apaga os critérios.
  nsmp: {
    equipes: values.equipes || [],
    prioridades: values.prioridadesNsmp || [],
    acessos: values.acessos || [],
    acessoVeiculo: acessoVeiculoParaApi(values.acessoVeiculo),
  },
  arquivos: FileService.getArquivosToUpload((values.arquivos || []).filter((arquivo) => arquivo.new)),
  arquivoIdsRemovidos: values.arquivoIdsRemovidos || [],
});

/**
 * Área administrativa (só módulo PROCEDIMENTO_DIGITACAO_ADMIN; sem ele as rotas dão 403).
 * Procedimento sem anotação devolve 200 com campos `null` e `anexos: []`, não 404.
 */
export const obterAdmin = async (id) => {
  const response = await BaseService.get({ url: `procedimentos-digitacao/${id}/admin` });
  return response?.data;
};

/** Dados administrativos do GET para o estado do formulário (`vigenciaDesde` fica como `yyyy-MM-dd`). */
export const valoresAdminDe = (admin, arquivos = []) => ({
  observacao: admin?.observacao || '',
  solicitante: admin?.solicitante || '',
  vigenciaDesde: admin?.vigenciaDesde || '',
  arquivos,
});

/** A seção só vai no PUT se algum campo mudou ou há anexo novo; senão o BE não toca na parte administrativa. */
export const adminAlterado = (admin, original) => {
  if (!admin || !original) return false;
  return (admin.observacao || '') !== original.observacao
    || (admin.solicitante || '') !== original.solicitante
    || (admin.vigenciaDesde || '') !== original.vigenciaDesde
    || (admin.arquivos || []).some((arquivo) => arquivo.new);
};

/** Bloco `admin` (PUT /{id}/admin e PUT /{id}). Substitui os três campos: reenviar o que veio do GET. Anexos só adicionam. */
const adminPayloadDe = (admin) => ({
  observacao: admin.observacao || null,
  solicitante: admin.solicitante || null,
  vigenciaDesde: admin.vigenciaDesde || null,
  arquivos: FileService.getArquivosToUpload((admin.arquivos || []).filter((arquivo) => arquivo.new)),
});

export const salvarAdmin = async (id, admin) => {
  const response = await BaseService.put({
    url: `procedimentos-digitacao/${id}/admin`,
    data: adminPayloadDe(admin),
  });
  const salvo = response?.data;
  const novos = (admin.arquivos || []).filter((arquivo) => arquivo.new);
  if (novos.length && salvo?.arquivosParaUpload?.length) {
    await FileService.uploadArquivos(novos, salvo.arquivosParaUpload);
  }
  return salvo;
};

export const historicoUrl = (id) => `procedimentos-digitacao/${id}/historico`;

/** Mesmo DTO do `nsirs/{id}/historico`. A tela usa `useAuditHistorico(historicoUrl(id))`. */
export const obterHistorico = async (id) => {
  const response = await BaseService.get({ url: historicoUrl(id) });
  return Array.isArray(response?.data) ? response.data : [];
};

export const salvarProcedimento = async (id, values) => {
  const data = payloadDe(values);
  if (!id) data.arquivoIdsRemovidos = [];
  // Só na edição e só se a seção foi alterada: bloco ausente = o BE não toca na parte administrativa.
  if (id && values.admin) data.admin = adminPayloadDe(values.admin);
  const response = id
    ? await BaseService.put({ url: `procedimentos-digitacao/${id}`, data })
    : await BaseService.post({ url: 'procedimentos-digitacao', data });
  const salvo = response?.data;
  // `arquivosParaUpload` mistura os anexos do conteúdo e os do admin: subir tudo o que vier.
  const novos = [
    ...(values.arquivos || []).filter((arquivo) => arquivo.new),
    ...((id && values.admin?.arquivos) || []).filter((arquivo) => arquivo.new),
  ];
  let uploadFalhou = false;
  if (novos.length && salvo?.arquivosParaUpload?.length) {
    // `uploadArquivos` devolve false (não lança) quando um upload falha. O cadastro já foi gravado:
    // quem chama avisa e fecha, para o POST não ser repetido e duplicar o procedimento.
    try {
      uploadFalhou = (await FileService.uploadArquivos(novos, salvo.arquivosParaUpload)) === false;
    } catch (error) {
      console.error(error);
      uploadFalhou = true;
    }
  }
  return { ...salvo, uploadFalhou };
};

export const inativarProcedimento = async (id) => {
  await BaseService.delete({ url: `procedimentos-digitacao/${id}` });
};

/** O BE só tem DELETE (inativar); reativar é um PUT com `ativo: true`, que substitui tudo: reenvia o que veio do GET. */
export const ativarProcedimento = async (id) => {
  const detalhe = await obterProcedimento(id);
  return salvarProcedimento(id, { ...valoresDeDetalhe(detalhe), ativo: true });
};

export { mensagemErro };
